import React, { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { ChevronDown, ChevronUp, LogOut, Settings, UserRound } from "lucide-react";
import { useLanguage } from "../ui/LanguageProvider";
import Spinner from "../ui/Spinner";
import { ROLE_LABEL, ROLE_LABEL_EN, useAuth } from "../auth/AuthContext";

/**
 * 사이드바 footer 계정 메뉴(앱 셸 공용) — 아바타 + 이름 + 역할, 위로 펼침.
 * 메뉴: (아바타 · 이름 · 이메일) / 마이페이지 · 설정 / 로그아웃(빨강). 앱 연동은 사용자 요청으로 뺐다(2026-10-06, 설정 안에서 들어간다).
 *
 * variant
 *  - sidebar: 펼친 사이드바(240px) 하단 44px 줄.
 *  - compact: 접힌 사이드바(56px) — 아바타만. 메뉴는 aside 의 overflow:hidden 에 잘리지 않게 fixed 로 띄운다.
 *  - bar: 상단 바(PageLayout) — 아래로 펼침.
 */
export default function AccountMenu({ variant = "sidebar" }: { variant?: "sidebar" | "bar" | "compact" } = {}) {
  const bar = variant === "bar";
  const compact = variant === "compact";
  const { t, language } = useLanguage();
  const { email, name, role, picture } = useAuth();
  const [open, setOpen] = useState(false);
  // compact 메뉴의 화면 좌표(fixed) — 열 때 버튼 위치로 잡는다.
  const [fixedPos, setFixedPos] = useState<{ left: number; bottom: number } | null>(null);
  // 구글 프로필 사진 — 실패 시 이니셜 폴백(f9ab3c6 회귀 복구). picture 바뀌면 에러 상태 초기화.
  const [imgError, setImgError] = useState(false);
  useEffect(() => { setImgError(false); }, [picture]);
  const ref = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);
  const [loggingOut, setLoggingOut] = useState(false);
  const handleLogout = async () => {
    if (loggingOut) return; // 두 번 누름 무시
    setLoggingOut(true); // 페이지를 떠나므로 되돌리지 않는다
    try { await fetch("/api/auth/logout", { method: "POST", credentials: "include" }); } finally { window.location.href = "/login"; }
  };
  const toggle = () => {
    if (!open && compact && triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setFixedPos({ left: r.left, bottom: window.innerHeight - r.top + 6 });
    }
    setOpen((v) => !v);
  };

  const displayName = name || email || t("게스트", "Guest");
  const roleLabel = role ? (language === "en" ? ROLE_LABEL_EN[role] : ROLE_LABEL[role]) : "";
  const avatar = (size: number) => (
    <span
      className="grid flex-shrink-0 place-items-center overflow-hidden rounded-pill bg-primary-subtle font-semibold text-primary-strong"
      style={{ width: size, height: size, fontSize: size >= 36 ? 14 : 12 }}
      aria-hidden
    >
      {picture && !imgError ? (
        <img src={picture} alt="" referrerPolicy="no-referrer" onError={() => setImgError(true)} className="block h-full w-full object-cover" />
      ) : (
        (name || email || "?").trim().charAt(0).toUpperCase()
      )}
    </span>
  );

  const rowClass = "flex h-[34px] w-full items-center gap-2.5 rounded-control px-2.5 text-left text-[13px] font-medium no-underline";
  const menuShadow = { boxShadow: "0 8px 24px rgba(31,42,68,.10)" };
  const menuClass = "z-50 rounded-card border border-hairline-strong bg-white p-1.5";
  const menuPosClass = bar
    ? "absolute right-0 top-[calc(100%+6px)] w-[240px]"
    : compact
      ? "fixed w-[240px]"
      : "absolute bottom-[calc(100%-2px)] left-2 right-2 mb-1.5";

  return (
    <div
      ref={ref}
      className={`ds-scope relative flex-shrink-0 ${bar ? "" : compact ? "flex justify-center border-0 border-t border-hairline py-2" : "border-0 border-t border-hairline p-2"}`}
    >
      {open ? (
        <div
          role="menu"
          className={`${menuPosClass} ${menuClass}`}
          style={compact && fixedPos ? { ...menuShadow, left: fixedPos.left, bottom: fixedPos.bottom } : menuShadow}
        >
          <div className="flex items-center gap-2.5 px-2.5 pb-2.5 pt-2">
            {avatar(36)}
            <div className="min-w-0">
              <div className="truncate text-[13px] font-semibold text-ink">{displayName}</div>
              {email ? <div className="truncate text-[12px] text-ink-muted">{email}</div> : null}
            </div>
          </div>
          <div className="mx-1 my-1.5 h-px bg-hairline" />
          <NavLink to="/settings/profile" role="menuitem" onClick={() => setOpen(false)} className={`${rowClass} text-ink-data hover:bg-surface-hover hover:text-ink`}>
            <UserRound size={16} className="text-ink-muted" aria-hidden />{t({ ko: "마이페이지", en: "My page" })}
          </NavLink>
          <NavLink to="/settings" role="menuitem" onClick={() => setOpen(false)} className={`${rowClass} text-ink-data hover:bg-surface-hover hover:text-ink`}>
            <Settings size={16} className="text-ink-muted" aria-hidden />{t({ ko: "설정", en: "Settings" })}
          </NavLink>
          <div className="mx-1 my-1.5 h-px bg-hairline" />
          <button type="button" role="menuitem" onClick={handleLogout} disabled={loggingOut} aria-busy={loggingOut || undefined} className={`${rowClass} text-danger-strong hover:bg-danger-subtle`}>
            {loggingOut ? <Spinner size={16} /> : <LogOut size={16} aria-hidden />}{loggingOut ? t({ ko: "로그아웃 중…", en: "Logging out…" }) : t({ ko: "로그아웃", en: "Log out" })}
          </button>
        </div>
      ) : null}
      {compact ? (
        <button
          ref={triggerRef}
          type="button"
          onClick={toggle}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={t({ ko: `계정 메뉴 · ${displayName}`, en: `Account menu · ${displayName}` })}
          title={displayName}
          className="grid h-9 w-9 place-items-center rounded-card-sm hover:bg-surface-hover"
        >
          {avatar(28)}
        </button>
      ) : (
        <button
          ref={triggerRef}
          type="button"
          onClick={toggle}
          aria-haspopup="menu"
          aria-expanded={open}
          className={`flex min-w-0 items-center gap-2.5 rounded-card-sm text-left hover:bg-surface-hover ${bar ? "h-9 w-auto px-1.5" : "h-11 w-full px-2"}`}
        >
          {avatar(28)}
          <span className="account-menu__meta grid min-w-0 flex-1">
            <span className="truncate text-[13px] font-semibold text-ink">{displayName}</span>
            {roleLabel ? <span className="truncate text-[12px] text-ink-muted">{roleLabel}</span> : null}
          </span>
          {bar ? (
            <ChevronDown size={14} className="flex-shrink-0 text-ink-muted transition-transform duration-150" style={{ transform: open ? "rotate(180deg)" : "none" }} />
          ) : (
            <ChevronUp size={14} className="flex-shrink-0 text-ink-muted transition-transform duration-150" style={{ transform: open ? "rotate(180deg)" : "none" }} />
          )}
        </button>
      )}
    </div>
  );
}
